import { Markdown as MarkdownView } from "#root/components/markdown";
import { Modal } from "#root/components/modal";
import { Invalid, Neutral, Valid } from "#root/utility/validators";
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CodeToggle,
  CreateLink,
  DiffSourceToggleWrapper,
  InsertCodeBlock,
  InsertImage,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  MDXEditor,
  type MDXEditorMethods,
  Separator,
  UndoRedo,
  codeBlockPlugin,
  codeMirrorPlugin,
  diffSourcePlugin,
  headingsPlugin,
  imagePlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { MdEdit } from "react-icons/md";
import { useFieldSet } from "./fieldset";

export function MarkdownField({
                                required,
                                placeholder = "No content yet — click Edit to get started.",
                              }: {
  required?: boolean;
  placeholder?: string;
}) {
  const fieldSet = useFieldSet();
  const initial = useMemo(() => {
    const v = fieldSet?.field?.value;
    return v ? String(v) : "";
  }, [fieldSet?.field?.value]);

  const [value, setValue] = useState(initial);
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const editorRef = useRef<MDXEditorMethods>(null);

  const { validity, hint } = useMemo(() => {
    if (!value || value.length === 0) {
      if (required) {
        return { validity: Invalid, hint: "Required" };
      }

      return { validity: Neutral, hint: "Optional" };
    }

    return { validity: Valid, hint: "Valid" };
  }, [value, required]);

  useEffect(() => {
    fieldSet?.mutate(obj => ({ ...obj, value, dirty, validity, hint }));
  }, [fieldSet, value, dirty, validity, hint]);

  const handleSave = () => {
    const next = editorRef.current?.getMarkdown() ?? value;
    setValue(next);
    setDirty(true);
    setOpen(false);
  };

  const borderColor = validity === Invalid ? "border-red-400" : validity === Valid ? "border-green-400" : "border-[#ddd]";

  return (
    <>
      <div className={`border ${borderColor} rounded bg-white`}>
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#eee] bg-[#fafafa]">
          <span className="text-sm text-[#666]">Markdown</span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex items-center gap-1 text-[#39f] hover:underline text-sm"
          >
            <MdEdit/> Edit
          </button>
        </div>
        <div className="px-3 py-2 max-h-64 overflow-auto">
          {value
            ? <MarkdownView markdown={value}/>
            : <p className="text-[#888] italic m-0">{placeholder}</p>}
        </div>
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Edit Markdown"
        footer={
          <>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-3 py-1.5 rounded border border-[#ddd] hover:bg-[#f5f5f5]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-3 py-1.5 rounded bg-[#39f] text-white hover:bg-[#2980d9]"
            >
              Save
            </button>
          </>
        }
      >
        <div className="mdx-editor-host h-full">
          <MDXEditor
            ref={editorRef}
            markdown={value}
            contentEditableClassName="prose max-w-none p-4 min-h-[60vh]"
            plugins={[
              headingsPlugin(),
              listsPlugin(),
              quotePlugin(),
              thematicBreakPlugin(),
              linkPlugin(),
              linkDialogPlugin(),
              imagePlugin(),
              tablePlugin(),
              codeBlockPlugin({ defaultCodeBlockLanguage: "" }),
              codeMirrorPlugin({
                codeBlockLanguages: {
                  "": "Plain text",
                  js: "JavaScript",
                  ts: "TypeScript",
                  jsx: "JSX",
                  tsx: "TSX",
                  css: "CSS",
                  html: "HTML",
                  json: "JSON",
                  bash: "Bash",
                  sql: "SQL",
                },
              }),
              markdownShortcutPlugin(),
              diffSourcePlugin({ diffMarkdown: initial, viewMode: "rich-text" }),
              toolbarPlugin({
                toolbarContents: () => (
                  <DiffSourceToggleWrapper>
                    <UndoRedo/>
                    <Separator/>
                    <BoldItalicUnderlineToggles/>
                    <CodeToggle/>
                    <Separator/>
                    <ListsToggle/>
                    <Separator/>
                    <BlockTypeSelect/>
                    <Separator/>
                    <CreateLink/>
                    <InsertImage/>
                    <Separator/>
                    <InsertTable/>
                    <InsertThematicBreak/>
                    <InsertCodeBlock/>
                  </DiffSourceToggleWrapper>
                ),
              }),
            ]}
          />
        </div>
      </Modal>
    </>
  );
}
